import type { MemberCountryCode } from "../location/memberLocation";
import {
  SUPPORT_CANONICAL_SECTION,
  getCanonicalSupportCategory,
} from "./supportCards";

type StaticSupportContentCard = {
  id: string;
  section: string;
  category: string;
  country_scope: MemberCountryCode;
  title: string;
  body: string;
  link_url: string | null;
  sort_order: number;
  last_reviewed_at: string;
  is_active: boolean;
};

type NewZealandSupportCardTuple = readonly [
  title: string,
  summary: string,
  route: string,
];

const NEW_ZEALAND_SUPPORT_REVIEWED_AT = "2026-04-21T19:00:00.000Z";

const buildBody = (summary: string, route: string) =>
  `${summary}\n\nOfficial routes:\n- ${route}`;

const buildCategoryCards = (
  slug: string,
  definitions: readonly NewZealandSupportCardTuple[],
): StaticSupportContentCard[] =>
  definitions.map(([title, summary, route], index) => ({
    id: `static-nz-${slug}-${index + 1}`,
    section: SUPPORT_CANONICAL_SECTION,
    category: getCanonicalSupportCategory(slug),
    country_scope: "NZ",
    title,
    body: buildBody(summary, route),
    link_url: null,
    sort_order: index + 1,
    last_reviewed_at: NEW_ZEALAND_SUPPORT_REVIEWED_AT,
    is_active: true,
  }));

const NEW_ZEALAND_SUPPORT_CARD_DEFINITIONS: Record<
  string,
  readonly NewZealandSupportCardTuple[]
> = {
  "money-benefits": [
    [
      "Sole Parent Support — how it works and when to claim",
      "Use the live Work and Income route to check who can get it, what shared-care and single-parent rules mean, what work preparation expectations may apply, and what to update when care, work, or study changes.",
      "Work and Income",
    ],
    [
      "Working for Families — the core family tax credits",
      "Start here for Family Tax Credit, In-Work Tax Credit, Minimum Family Tax Credit, and the practical need to keep income and family details current so year-end adjustments do not catch you out.",
      "Inland Revenue",
    ],
    [
      "Best Start — extra help in your child’s first 3 years",
      "Use the live Inland Revenue page for the current rules, because start dates, income settings, and how it interacts with paid parental leave can change over time.",
      "Inland Revenue",
    ],
    [
      "Child support — private arrangements or Inland Revenue collection",
      "Use this to understand private agreements versus formula assessment, shared-care thresholds, arrears, and what to do when care or income changes.",
      "Inland Revenue",
    ],
    [
      "If you separate — update Inland Revenue and Work and Income early",
      "After a breakup, update relationship status, address, care percentages, Working for Families, child support, and any other payments early so the system reflects real life.",
      "Inland Revenue",
    ],
    [
      "Accommodation Supplement — help with rent, board or home costs",
      "Use the live Work and Income route because area settings, income rules, and what housing costs count can change. This is the right next stop when housing costs shift.",
      "Work and Income",
    ],
    [
      "Childcare Subsidy — help with pre-school childcare costs",
      "Start here for childcare help before school age, including how work or study changes matter and how this sits alongside other early-learning support.",
      "Work and Income",
    ],
    [
      "OSCAR Subsidy — before-school, after-school and holiday care",
      "Use this for approved out-of-school and holiday-care support, especially when work patterns, school holidays, or care gaps make ordinary routines hard to hold together.",
      "Work and Income",
    ],
    [
      "FamilyBoost — help with ECE bills",
      "Use the live Inland Revenue page for the current rules and claiming process, especially where receipts and timing matter alongside 20 Hours ECE and Childcare Subsidy.",
      "Inland Revenue",
    ],
    [
      "Paid parental leave — what it covers",
      "Use the live Inland Revenue route for employee and self-employed eligibility, application timing, and how it fits with Best Start and Working for Families.",
      "Inland Revenue",
    ],
    [
      "Disability and caring support for children — where to start",
      "Start here for Child Disability Allowance, Disability Allowance, carer support, and the point where ongoing need may mean looking more closely at wider caring routes.",
      "Work and Income",
    ],
    [
      "Emergency help with essentials — food, grants and one-off costs",
      "Use this if food, urgent bills, or an essential household cost has pushed things past coping point. The live Work and Income page is safer than repeating fast-moving settings in the app.",
      "Work and Income",
    ],
    [
      "Temporary Additional Support — when essentials still do not fit the budget",
      "Use this when essential regular costs still do not fit after you have checked the bigger payments, and remember it is a temporary support that can change when rent or income changes.",
      "Work and Income",
    ],
    [
      "Budgeting on one income — start with Sorted",
      "Sorted is a calm national starting point for budgeting around school costs, transport, childcare, and irregular household pressure without pretending the month is simple.",
      "Sorted",
    ],
    [
      "Debt and hardship help — talk to MoneyTalks early",
      "If arrears, creditors, or household bills are becoming too much, MoneyTalks is a stronger first route than waiting for a crisis because early budgeting and debt support usually gives you more options.",
      "MoneyTalks",
    ],
    [
      "Rates rebate — if you own your home",
      "Use the New Zealand Government starting point to understand the scheme, then follow through to your local council because the final decision and local administration sit there.",
      "New Zealand Government",
    ],
  ],
  housing: [
    [
      "Renting as a single parent — your rights under NZ tenancy law",
      "Tenancy law is national in New Zealand, so Tenancy Services is the right first stop for agreements, rent, privacy, inspections, and the core rights and responsibilities that apply everywhere.",
      "Tenancy Services",
    ],
    [
      "Bond, tenancy agreements and evidence at the start",
      "Use this to check bond limits, lodgement, what to look for before signing, and why photos and written evidence at the beginning matter later.",
      "Tenancy Services",
    ],
    [
      "Healthy Homes standards — warmth, damp and ventilation",
      "Use the national Healthy Homes route for the current minimum standards on heating, insulation, ventilation, moisture, and draught stopping, and what to do if the property falls short.",
      "Tenancy Services",
    ],
    [
      "Repairs, maintenance and urgent work",
      "Use this to understand what landlords must fix, what tenants should report in writing, what is urgent, and when it is time to escalate rather than keep waiting.",
      "Tenancy Services",
    ],
    [
      "Rent increases, arrears and hardship",
      "Start here for national notice rules, what to do if arrears are building, and why acting early is safer than waiting for the situation to become formal.",
      "Tenancy Services",
    ],
    [
      "Ending a tenancy, notice periods and unlawful pressure",
      "Use the national tenancy route to check how periodic notices work, what written notice must include, and when pressure or retaliation may be unlawful.",
      "Tenancy Services",
    ],
    [
      "Tenancy Tribunal and mediation — when to escalate",
      "Use this to understand what mediation and the Tribunal can do, what evidence helps, and when a legally binding order may be the next step.",
      "Tenancy Services",
    ],
    [
      "Emergency housing — if you have nowhere to stay",
      "If you need somewhere to stay now or in the next few days, use the Work and Income emergency-housing route first. Access can still vary by local availability and urgency.",
      "Work and Income",
    ],
    [
      "Public housing — how the register works",
      "Use this to understand the social housing register, how applications work, and why availability and waiting times still vary by region and provider even though the entry route is national.",
      "Work and Income",
    ],
    [
      "Family violence and housing safety routes",
      "If safety is part of the housing problem, start with national family-violence support and urgent housing help. Specialist service coverage and response may still vary locally.",
      "Are You OK",
    ],
    [
      "Moving house with children — the legal and practical bits",
      "A move can affect day-to-day care, school, and contact arrangements. Use the Ministry of Justice route early if consent, relocation, or overseas travel may become part of the picture.",
      "Ministry of Justice",
    ],
    [
      "First-home help — KiwiSaver and Kāinga Ora options",
      "Use the national government starting point for first-home help so you can check current routes without relying on out-of-date thresholds or scheme settings.",
      "New Zealand Government",
    ],
    [
      "Power, water and urgent household bills — get help early",
      "If essential bills are pushing you under, start with Work and Income and budgeting help early rather than waiting until disconnection or debt pressure becomes the main problem.",
      "Work and Income",
    ],
  ],
  legal: [
    [
      "Separation and divorce — the basics in New Zealand",
      "Start here to separate the legal meaning of separation and dissolution from the other practical tasks, so you do not assume divorce sorts parenting, property, and money all by itself.",
      "Ministry of Justice",
    ],
    [
      "Separation agreements and relationship property — separate issues from divorce",
      "Use this to understand what a Separation Order does and does not cover, and why parenting, property, and practical arrangements often need their own path.",
      "Ministry of Justice",
    ],
    [
      "Parenting arrangements — day-to-day care, contact and guardianship",
      "Use the national family-law route to understand parenting arrangements, best interests, and how private parenting plans fit before court becomes necessary.",
      "Ministry of Justice",
    ],
    [
      "Parenting Through Separation and Family Dispute Resolution",
      "This is often the first structured step when agreement is hard, unless family violence or urgency changes the route. Start here before assuming court is the only option.",
      "Ministry of Justice",
    ],
    [
      "Parenting Orders and court orders — when court is needed",
      "Use this to understand who can apply, what certificates are usually needed first, what urgent applications look like, and how filing and service work.",
      "Ministry of Justice",
    ],
    [
      "Child support and care arrangements — different systems",
      "Child support and parenting arrangements are linked in real life but run through different systems. Start with the Inland Revenue route when the payment side is the part that needs attention.",
      "Inland Revenue",
    ],
    [
      "Moving within New Zealand or overseas with children",
      "If relocation changes care or contact, or if you are worried a child may be taken overseas, use the national Ministry of Justice route early rather than after travel is booked.",
      "Ministry of Justice",
    ],
    [
      "Child passports and travel consent",
      "Use the New Zealand Passports route to understand who can consent to a child passport and what extra documents may be useful before travel.",
      "New Zealand Passports",
    ],
    [
      "Family violence — legal options and specialist support",
      "Start here for Protection Orders, urgent applications, and specialist support alongside the legal route. Local specialist-service coverage can still vary in practice.",
      "Ministry of Justice",
    ],
    [
      "Free legal help — legal aid, Community Law and CAB",
      "Use this as the national starting point for legal aid and free legal help. Community Law and CAB coverage is national but the nearest clinic, outreach, or appointment route may vary locally.",
      "Ministry of Justice",
    ],
    [
      "Wills, guardianship and enduring powers of attorney",
      "Single parents should not leave wills and enduring powers of attorney until later. Use the government starting point first, then move into legal help if family arrangements are complex.",
      "New Zealand Government",
    ],
  ],
  wellbeing: [
    [
      "When the house is quiet — coping when the children are away",
      "If the quiet after handover lands harder than you expected, 1737 is a safe first support when you need to talk now rather than sit in it alone.",
      "1737",
    ],
    [
      "Loneliness and isolation — rebuilding adult connection",
      "Use this when separation or solo parenting has shrunk your world. Rebuilding support is common, not a personal failure, and it usually starts with one usable connection rather than a big network.",
      "Family Services Directory",
    ],
    [
      "Burnout — noticing it earlier",
      "If chronic stress is showing up as numbness, irritation, forgetfulness, or constant overload, start with national health support earlier than you think.",
      "Health New Zealand",
    ],
    [
      "Guilt and impossible standards — getting back to good enough",
      "Use this when parenting guilt or pressure is crowding out the practical support you need. Parent Help is a stronger first route than trying to talk yourself out of it alone.",
      "Parent Help",
    ],
    [
      "After separation — rebuilding life one bit at a time",
      "After separation, legal, parenting, money, and wellbeing questions often move together. Start with one sensible next support rather than trying to solve everything at once.",
      "Ministry of Justice",
    ],
    [
      "Parenting support when you are stretched",
      "If you need a calm person more than a perfect answer, use national parenting support early instead of waiting for the day to get worse.",
      "Parent Help",
    ],
    [
      "Mental health support — start with local health support or 1737",
      "Start with national health support or 1737 when the load feels too heavy. Use a GP or local service where that is the right next step.",
      "Health New Zealand",
    ],
    [
      "Crisis support — when you need help now",
      "If tonight feels unsafe, unbearable, or too much to hold, use 1737 now. You do not need the practical side sorted before reaching for immediate support.",
      "1737",
    ],
    [
      "Family violence support — when safety is part of the picture",
      "Use specialist family-violence support when safety is part of the picture. National support is there, but local specialist-service pathways can still vary.",
      "Are You OK",
    ],
    [
      "Sleep and exhaustion — when tired turns into too much",
      "If exhaustion is running the house, use a trusted parenting and child-health route rather than treating it as something you just need to push through.",
      "PlunketLine",
    ],
    [
      "Exercise and movement on a single-parent schedule",
      "Use the national health route for realistic movement and wellbeing support that fits real life rather than ideal routines.",
      "Health New Zealand",
    ],
  ],
  "education-send": [
    [
      "School enrolment — how it works in New Zealand",
      "Use the national government starting point for starting school, compulsory attendance, moving, and enrolment basics before you get pulled into local detail.",
      "New Zealand Government",
    ],
    [
      "School zones and enrolment schemes — check the local rules",
      "School zones and out-of-zone places vary by school and location, so use the Ministry of Education route first and check the local scheme before you assume a place is available.",
      "Ministry of Education",
    ],
    [
      "Starting school or changing schools — organise earlier than you think",
      "Use this when a move or separation means a child is starting or changing school, so you can get paperwork, routines, and key information in place before it becomes urgent.",
      "New Zealand Government",
    ],
    [
      "School costs, donations and what schools can ask for",
      "Use the Ministry of Education route to understand donations, what stays optional, and how school costs work in practice without relying on hearsay.",
      "Ministry of Education",
    ],
    [
      "School transport assistance — distance, eligibility and reviews",
      "School transport help depends on distance, school type, and route availability, so local access can still vary even though the national rules sit with the Ministry of Education.",
      "Ministry of Education",
    ],
    [
      "Stand-downs, suspensions, exclusions and expulsions",
      "Use this to understand what each process means, what your child is entitled to, and how to prepare for school board meetings if discipline has escalated.",
      "Ministry of Education",
    ],
    [
      "Home education — what the legal route looks like",
      "Use the national Ministry of Education route first so you understand home education properly before treating it as the same thing as distance schooling.",
      "Ministry of Education",
    ],
    [
      "Types of early learning — know the options first",
      "Use this to compare centre-based, home-based, and whānau-led early-learning options before you narrow down what fits your child and your real week.",
      "Ministry of Education",
    ],
    [
      "Choosing and starting an early learning service",
      "Use the Ministry of Education route to compare services, fees, waiting lists, and settling-in expectations before you commit.",
      "Ministry of Education",
    ],
    [
      "20 Hours ECE — what it covers",
      "Use the national Ministry of Education page for the current explanation of 20 Hours ECE instead of relying on old assumptions about what is or is not included.",
      "Ministry of Education",
    ],
    [
      "Childcare Subsidy, FamilyBoost and 20 Hours ECE — using them together",
      "This is where parents often get confused, so start with the Work and Income route and check the live government information instead of trying to calculate it from memory.",
      "Work and Income",
    ],
    [
      "Holiday care and OSCAR — planning around school breaks",
      "Use the Work and Income route when school-age care during holidays is the real pressure point, and plan early because programme availability can tighten fast.",
      "Work and Income",
    ],
    [
      "Learning support — start with the school or kura and ask early",
      "Use the Ministry of Education route when you are worried about learning, communication, or behaviour, and ask what support can happen now rather than only after a diagnosis.",
      "Ministry of Education",
    ],
    [
      "Early intervention — when concerns start before or around school entry",
      "Use this when support needs are showing before or around school start. Transition planning matters, and early support should not get lost in the handover.",
      "Ministry of Education",
    ],
    [
      "Learning support when your child moves to school",
      "Use the Ministry of Education route to keep support from falling away during the move from early learning into school or kura.",
      "Ministry of Education",
    ],
    [
      "ORS — when needs are highest",
      "Use the national ORS route to understand what it is for and how approved support follows a learner across schools once it is in place.",
      "Ministry of Education",
    ],
    [
      "School transport for safety or mobility needs — SESTA",
      "Use the Ministry of Education route first for specialised school transport. Eligibility is national, but the practical transport pathway can still depend on route availability.",
      "Ministry of Education",
    ],
    [
      "Disability support beyond school — DSS, NASC and local pathways",
      "Use Disability Support Services as the national starting point. Local providers, wait times, and access pathways can still vary by area and service availability.",
      "Disability Support Services",
    ],
    [
      "Autism support — services and where to start",
      "Use Disability Support Services first to understand how disability support may fit alongside school and health pathways.",
      "Disability Support Services",
    ],
    [
      "ADHD support — local information and community help",
      "ADHD New Zealand is a stronger specialist route than a general page when you need New Zealand-specific ADHD information and community support while you work out next steps.",
      "ADHD New Zealand",
    ],
    [
      "Parent-to-parent support for disability and complex needs",
      "Parent to Parent is a stronger national specialist route when you need peer support, information, and whānau connection while navigating disability, diagnosis, or school support.",
      "Parent to Parent",
    ],
  ],
  work: [
    [
      "Flexible working — asking for flexibility",
      "Use Employment New Zealand for the formal route to ask in writing, what must be included, and what a valid refusal looks like.",
      "Employment New Zealand",
    ],
    [
      "Parents and carers at work — leave and practical protections",
      "Start here if you need time, flexibility, or a fair process rather than goodwill. This is the right national route for carers trying to make work fit real family life.",
      "Employment New Zealand",
    ],
    [
      "Family violence leave and short-term flexible work",
      "Use the live Employment New Zealand route for current leave settings and short-term flexible-work rights instead of relying on old summaries.",
      "Employment New Zealand",
    ],
    [
      "Sick leave — when your child is sick or care falls through",
      "Use this to understand who qualifies, how leave builds up, and how dependants are covered when the child is unwell rather than you.",
      "Employment New Zealand",
    ],
    [
      "Part-time, casual and fixed-term work — check the terms carefully",
      "Use Employment New Zealand to check hours, availability, leave, and security carefully before you agree to a role that may look flexible but not work in practice.",
      "Employment New Zealand",
    ],
    [
      "Minimum wage and being paid correctly",
      "Use the live Employment New Zealand page for the current minimum-wage settings and what to do if you think you are being underpaid.",
      "Employment New Zealand",
    ],
    [
      "Redundancy, redeployment and final pay",
      "Use this to understand what genuine redundancy means, what is and is not automatic, and why final pay should be checked closely.",
      "Employment New Zealand",
    ],
    [
      "Discrimination because you are a parent or carer",
      "Start here when family status or caring duties are affecting the way you are treated at work, and you need the national employment-rights route rather than informal reassurance.",
      "Employment New Zealand",
    ],
    [
      "Personal grievances, early resolution and mediation",
      "Use the national Employment New Zealand route early when time limits matter or a workplace problem is becoming formal.",
      "Employment New Zealand",
    ],
    [
      "Returning to work after a gap",
      "Use this when you are trying to work out whether a job actually fits childcare, transport, hours, confidence, and household income in real life.",
      "Employment New Zealand",
    ],
    [
      "Employee or contractor — the difference matters",
      "Use Employment New Zealand first if the label on the contract and the reality of the work do not seem to match, because the answer changes rights, leave, tax, and ACC.",
      "Employment New Zealand",
    ],
    [
      "Starting self-employment — tax, GST and records",
      "Use Inland Revenue first for the tax side of self-employment so you can get your records, GST, and obligations straight before bad habits set in.",
      "Inland Revenue",
    ],
    [
      "Tax basics for sole traders — do not guess later",
      "Use business.govt.nz for a plainer-language starting point on tax basics, expenses, and self-employed admin when Inland Revenue feels too dense as a first read.",
      "business.govt.nz",
    ],
    [
      "Provisional tax — plan for it before it bites",
      "Use the live Inland Revenue route for current provisional-tax rules rather than relying on copied thresholds or old examples.",
      "Inland Revenue",
    ],
    [
      "ACC cover and levies if you work for yourself",
      "Start with ACC when you need to understand cover and levies as a self-employed person, especially if illness, injury, or care pressure could stop work fast.",
      "ACC",
    ],
  ],
};

export const getNewZealandStaticSupportCategoryCards = (
  slug: string,
  countryCode?: string | null,
): StaticSupportContentCard[] => {
  if (countryCode !== "NZ") {
    return [];
  }

  const definitions = NEW_ZEALAND_SUPPORT_CARD_DEFINITIONS[slug];
  return definitions ? buildCategoryCards(slug, definitions) : [];
};
